import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./BitacoraPage.css";
import './SharedLoading.css';
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import API_BASE_URL from "../config";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const STATUS_LABELS = {
  CONCLUIDO: "Concluido",
  ASIGNADO: "Asignado",
  ENPROCESO: "En proceso",
  EN_PROCESO: "En proceso",
  STDBY: "Stand by",
  PAUSE: "En pausa",
  NOSHOW: "No show",
  ENPROGRESO: "En progreso",
  SIN_STATUS: "Sin estado",
};

const STATUS_FILTERS = [
  { valor: "todos", etiqueta: "Todos" },
  { valor: "CONCLUIDO", etiqueta: "Concluidos" },
  { valor: "ASIGNADO", etiqueta: "Asignados" },
  { valor: "ENPROCESO", etiqueta: "En proceso" },
  { valor: "STDBY", etiqueta: "Stand by" },
  { valor: "PAUSE", etiqueta: "En pausa" },
  { valor: "NOSHOW", etiqueta: "No show" },
];

const normalizarStatus = (status) => {
  if (!status) return "SIN_STATUS";
  const upper = status.toString().trim().toUpperCase();
  if (upper === "EN PROCESO") return "ENPROCESO";
  if (upper === "ASIGNADO") return "ASIGNADO";
  return upper;
};

const TimeAgo = ({ timestamp }) => {
  const [timeAgo, setTimeAgo] = useState("");

  useEffect(() => {
    if (timestamp && moment(timestamp).isValid()) {
      const update = () => setTimeAgo(moment(timestamp).fromNow(true));
      update();
      const intervalId = setInterval(update, 60000);
      return () => clearInterval(intervalId);
    }
    setTimeAgo("");
    return undefined;
  }, [timestamp]);

  if (!timeAgo) return null;
  return <span className="bitacora-flight__time">{timeAgo}</span>;
};

const BitacoraPage = () => {
  const token = useSelector((state) => state.auth.token);
  const decoded = token ? jwtDecode(token) : {};
  const estacionUsuario = decoded.estacion?.trim().toUpperCase() || "SIN_ESTACION";

  const [bitacoraData, setBitacoraData] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [servicioDetalle, setServicioDetalle] = useState(null);
  const [modalServicioAbierto, setModalServicioAbierto] = useState(false);
  const [modalPendienteVisible, setModalPendienteVisible] = useState(false);
  const [busquedaSiglas, setBusquedaSiglas] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [operadoresInfo, setOperadoresInfo] = useState({});
  const operadoresCargandoRef = useRef(new Set());
  const [vista, setVista] = useState("tarjetas");

  const [modoPendiente, setModoPendiente] = useState("STDBY");
  const [ubicacionPax, setUbicacionPax] = useState("");
  const [mostrarModalConexion, setMostrarModalConexion] = useState(false);
  const [datosConexion, setDatosConexion] = useState({
    aerolinea: "",
    vuelo: "",
    servicio: null,
  });
  const [busquedaVuelo, setBusquedaVuelo] = useState("");
  const [modalBusquedaVueloVisible, setModalBusquedaVueloVisible] = useState(false);
  const [resultadosBusquedaVuelo, setResultadosBusquedaVuelo] = useState([]);
  const [usuariosBusqueda, setUsuariosBusqueda] = useState([]);
  const parseFecha = (valor) => {
    if (!valor) return null;
    const limpio = typeof valor === "string" ? valor.trim() : valor;
    const estricta = moment(limpio, ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
    if (estricta.isValid()) return estricta;
    const conZona = moment.parseZone(limpio);
    if (conZona.isValid()) return conZona;
    const flexible = moment(limpio);
    return flexible.isValid() ? flexible : null;
  };

  const formatearFecha = (valor) => {
    const m = parseFecha(valor);
    if (m) return m.format("DD/MM HH:mm");
    return valor || "Sin hora";
  };

  const fetchBitacoraData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-vuelos`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const filtrados = response.data.filter(
        (item) => item.estacion?.trim().toUpperCase() === estacionUsuario
      );

      setBitacoraData(filtrados);
    } catch (error) {
      console.error("Error al obtener la bitácora:", error);
    } finally {
      setLoadingPage(false);
    }
  };

  const abrirDetalleServicio = async (noServicio) => {
    try {
      const response = await axios.get(`${API_BASE_URL}api/servicios/${noServicio}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setServicioDetalle(response.data);
      setModalServicioAbierto(true);
    } catch (error) {
      console.error("Error al obtener detalle del servicio:", error);
    }
  };

  const cerrarServicio = async (noServicio) => {
    try {
      await axios.put(
        `${API_BASE_URL}api/servicios/${noServicio}`,
        {
          horaFin: moment().format("YYYY-MM-DD HH:mm:ss"),
          statusServicio: "CONCLUIDO",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("✅ Servicio cerrado correctamente");
      abrirDetalleServicio(noServicio);
      fetchBitacoraData();
    } catch (error) {
      console.error("Error al cerrar servicio:", error);
      toast.error("❌ No se pudo cerrar el servicio");
    }
  };

  const marcarNoShow = async (noServicio) => {
    try {
      await axios.put(
        `${API_BASE_URL}api/servicios/${noServicio}`,
        {
          horaFin: moment().format("YYYY-MM-DD HH:mm:ss"),
          statusServicio: "NOSHOW",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.info("🚫 Servicio marcado como NOSHOW");
      abrirDetalleServicio(noServicio);
      fetchBitacoraData();
    } catch (error) {
      console.error("Error al marcar NOSHOW:", error);
      toast.error("❌ No se pudo actualizar a NOSHOW");
    }
  };

  const actualizarPendiente = async (noServicio) => {
    if (!ubicacionPax.trim()) {
      toast.warn("⚠️ Debes indicar dónde dejaste al pasajero.");
      return;
    }

    const payload = {
      statusServicio: modoPendiente,
      checkpoint: ubicacionPax,
    };

    if (modoPendiente === "STDBY") {
      payload.noColaborador = 0;
    }

    try {
      await axios.put(`${API_BASE_URL}api/servicios/${noServicio}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("✅ Servicio actualizado correctamente");
      setModalPendienteVisible(false);
      setUbicacionPax("");
      setModoPendiente("STDBY");
      abrirDetalleServicio(noServicio);
      fetchBitacoraData();
    } catch (error) {
      console.error("Error al actualizar a pendiente:", error);
      toast.error("❌ No se pudo actualizar el servicio");
    }
  };

  const abrirModalConexion = (servicio) => {
    setDatosConexion({ aerolinea: "", vuelo: "", servicio });
    setMostrarModalConexion(true);
  };

  const cerrarModalConexion = () => {
    setMostrarModalConexion(false);
    setDatosConexion({ aerolinea: "", vuelo: "", servicio: null });
  };

  const buscarPorVuelo = async () => {
    const vueloQuery = busquedaVuelo.trim().toUpperCase();
    if (!vueloQuery) {
      toast.warn("Ingresa un número de vuelo para buscar.");
      return;
    }

    const ahora = moment();
    const inicioVentana = ahora.clone().subtract(9, "hours");
    const finVentana = ahora.clone().endOf("day");
    const extraerHora = (obj, posiblesClaves = []) => {
      if (!obj) return null;
      for (const clave of posiblesClaves) {
        if (obj[clave]) return obj[clave];
      }
      return null;
    };

    const coincidenciasBase = bitacoraDecorada.flatMap((item) => {
      const operadorId = item.noColaborador;
      const operadorInfo = operadorId ? operadoresInfo[operadorId] : null;
      const nombreOperador = operadorInfo
        ? [operadorInfo.nombre, operadorInfo.apellidos]
            .map((parte) => (parte || "").trim())
            .filter(Boolean)
            .join(" ")
        : null;
      const nombreFallback = item.siglas
        ? `Operador ${item.siglas}`
        : operadorId
        ? `Operador ${operadorId}`
        : "Operador";

      const servicios = (item.vuelos || []).filter((vuelo) =>
        vuelo.numero?.toString().toUpperCase().includes(vueloQuery)
      );

      return servicios
        .map((vuelo) => {
          const horaInicioVuelo =
            vuelo.hora_inicio ||
            vuelo.horaInicio ||
            vuelo.hora_asignacion ||
            vuelo.horaInicioAsignacion ||
            vuelo.horaInicioServicio ||
            vuelo.horaAsignacion ||
            vuelo.horaInicioStr ||
            vuelo.hora_inicio_str ||
            item.horaInicio ||
            item.hora_inicio ||
            item.horaInicioServicio ||
            item.hora_inicio_servicio;

          const horaFinVuelo =
            vuelo.horaFin ||
            vuelo.horafin ||
            vuelo.hora_fin ||
            vuelo.horaFinServicio ||
            vuelo.hora_fin_servicio ||
            vuelo.horaFinServicioStr ||
            vuelo.horaFinStr ||
            vuelo.hora_fin_str ||
            item.horaFin ||
            item.hora_fin ||
            item.horaFinServicio ||
            item.hora_fin_servicio ||
            item.hora_fin_str ||
            item.horaFinStr;

          const momentoInicio = parseFecha(horaInicioVuelo);
          const momentoFin = parseFecha(horaFinVuelo);
          const horaReferencia = horaInicioVuelo || horaFinVuelo;
          const momentoRef = horaReferencia ? parseFecha(horaReferencia) : null;

          if (!momentoRef || !momentoRef.isBetween(inicioVentana, finVentana, undefined, "[]")) {
            return null;
          }

          let duracionMinutos = null;
          let duracionLabel = "";
          if (momentoInicio && momentoFin) {
            duracionMinutos = momentoFin.diff(momentoInicio, "minutes");
            if (duracionMinutos >= 0) {
              const horas = Math.floor(duracionMinutos / 60);
              const minutos = duracionMinutos % 60;
              duracionLabel = horas > 0 ? `${horas}h ${minutos}m` : `${minutos}m`;
            }
          }

          return {
            servicioId: vuelo.id,
            vueloNumero: vuelo.numero,
            statusEtiqueta: vuelo.statusEtiqueta,
            statusClass: vuelo.statusClass,
            horaReferencia,
            horaInicio: horaInicioVuelo,
            horaFin: horaFinVuelo,
            duracionMinutos,
            duracionLabel,
            aerolinea: vuelo.aerolinea || item.aerolinea,
            origenVuelo: vuelo.origenVuelo || item.origenVuelo,
            destinoVuelo: vuelo.destinoVuelo || item.destinoVuelo,
            horaVuelo: vuelo.horaVuelo || item.horaVuelo,
            tipoService: vuelo.tipoService || item.tipoService,
            int_nac: vuelo.int_nac || item.int_nac,
            tipoSilla: vuelo.tipoSilla || item.tipoSilla,
            usuarioInicio: vuelo.usuarioInicio || item.usuarioInicio,
            nombrePax: vuelo.nombrePax || item.nombrePax,
            operadorId,
            siglas: item.siglas,
            nombreOperador: nombreOperador || nombreFallback,
          };
        })
        .filter(Boolean);
    });

    if (!coincidenciasBase.length) {
      toast.info("No encontramos servicios para ese vuelo.");
      setResultadosBusquedaVuelo([]);
      setModalBusquedaVueloVisible(false);
      return;
    }

    const faltantesHoras = coincidenciasBase.filter(
      (c) => !parseFecha(c.horaInicio) || !parseFecha(c.horaFin)
    );

    const detallesMap = {};

    if (faltantesHoras.length) {
      await Promise.all(
        faltantesHoras.map(async (c) => {
          try {
            const { data } = await axios.get(`${API_BASE_URL}api/servicios/${c.servicioId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            detallesMap[c.servicioId] = data;
          } catch (error) {
            console.error(`Error al obtener detalle de servicio ${c.servicioId}:`, error);
          }
        })
      );
    }

    const usuariosUnicos = [];
    const agregarUsuario = (u) => {
      if (u && !usuariosUnicos.includes(u)) usuariosUnicos.push(u);
    };

    const resultados = coincidenciasBase.map((c) => {
      const detalle = detallesMap[c.servicioId];
      const horaInicioDetalle = extraerHora(detalle, [
        "horaInicio",
        "hora_inicio",
        "horaInicioServicio",
        "hora_inicio_servicio",
        "horaInicioStr",
        "hora_inicio_str",
      ]);
      const horaFinDetalle = extraerHora(detalle, [
        "horaFin",
        "hora_fin",
        "horaFinServicio",
        "hora_fin_servicio",
        "horaFinStr",
        "hora_fin_str",
      ]);

      const horaInicioFinal = parseFecha(c.horaInicio) ? c.horaInicio : horaInicioDetalle || c.horaInicio;
      const horaFinFinal = parseFecha(c.horaFin) ? c.horaFin : horaFinDetalle || c.horaFin;

      const momentoInicioFinal = parseFecha(horaInicioFinal);
      const momentoFinFinal = parseFecha(horaFinFinal);

      let duracionMinutos = c.duracionMinutos;
      let duracionLabel = c.duracionLabel;

      if (momentoInicioFinal && momentoFinFinal) {
        const diffMin = momentoFinFinal.diff(momentoInicioFinal, "minutes");
        if (diffMin >= 0) {
          duracionMinutos = diffMin;
          const horas = Math.floor(diffMin / 60);
          const minutos = diffMin % 60;
          duracionLabel = horas > 0 ? `${horas}h ${minutos}m` : `${minutos}m`;
        }
      }

      agregarUsuario(detalle?.usuarioInicio);
      agregarUsuario(c.usuarioInicio);

      return {
        ...c,
        horaInicio: horaInicioFinal,
        horaFin: horaFinFinal,
        horaInicioLabel: formatearFecha(horaInicioFinal),
        horaFinLabel: formatearFecha(horaFinFinal),
        aerolinea: c.aerolinea || detalle?.aerolinea,
        origenVuelo: c.origenVuelo || detalle?.origenVuelo,
        destinoVuelo: c.destinoVuelo || detalle?.destinoVuelo,
        horaVuelo: c.horaVuelo || detalle?.horaVuelo,
        tipoService: c.tipoService || detalle?.tipoService,
        int_nac: c.int_nac || detalle?.int_nac,
        tipoSilla: c.tipoSilla || detalle?.tipoSilla,
        usuarioInicio: c.usuarioInicio || detalle?.usuarioInicio,
        nombrePax: c.nombrePax || detalle?.nombrePax,
        duracionMinutos,
        duracionLabel,
      };
    });

    setUsuariosBusqueda(usuariosUnicos);
    setResultadosBusquedaVuelo(resultados);
    setModalBusquedaVueloVisible(true);
  };

  const guardarConexion = async () => {
    const aerolinea = datosConexion.aerolinea.toUpperCase().trim();
    const vuelo = datosConexion.vuelo.trim();

    if (!aerolinea || !vuelo) {
      toast.error("Por favor completa ambos campos: aerolínea y número de vuelo.");
      return;
    }

    const conexionFinal = `${aerolinea}${vuelo}`;

    try {
      await axios.put(
        `${API_BASE_URL}api/servicios/${datosConexion.servicio._id}`,
        {
          conexionCon: conexionFinal,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(`Conexión registrada: ${conexionFinal}`);
      cerrarModalConexion();
      abrirDetalleServicio(datosConexion.servicio.noServicio);
      fetchBitacoraData();
    } catch (err) {
      console.error("Error al guardar conexión:", err);
      toast.error("Error al guardar conexión");
    }
  };

  useEffect(() => {
    fetchBitacoraData();
  }, []);

  const bitacoraDecorada = useMemo(() => {
    return bitacoraData.map((item) => {
      const vuelosDecorados = (item.vuelos || []).map((vuelo) => {
        const statusNormalizado = normalizarStatus(vuelo.status);
        return {
          ...vuelo,
          statusNormalizado,
          statusEtiqueta: STATUS_LABELS[statusNormalizado] || vuelo.status || "Sin estado",
          statusClass: `status-${statusNormalizado.toLowerCase()}`,
        };
      });

      const totalVuelos = vuelosDecorados.length;
      const concluidos = vuelosDecorados.filter((v) => v.statusNormalizado === "CONCLUIDO").length;
      const operaciones = vuelosDecorados.filter((v) => v.statusNormalizado === "ENPROCESO").length;
      const asignados = vuelosDecorados.filter((v) => v.statusNormalizado === "ASIGNADO").length;
      const noShow = vuelosDecorados.filter((v) => v.statusNormalizado === "NOSHOW").length;
      const noConcluidos = vuelosDecorados.filter(
        (v) => v.statusNormalizado !== "CONCLUIDO" && v.statusNormalizado !== "NOSHOW"
      ).length;
      const standBy = vuelosDecorados.filter((v) => v.statusNormalizado === "STDBY").length;
      const pausados = vuelosDecorados.filter((v) => v.statusNormalizado === "PAUSE").length;
      const noColaborador =
        item.noColaborador ||
        item.colaborador ||
        vuelosDecorados.find((vuelo) => vuelo.noColaborador)?.noColaborador ||
        vuelosDecorados.find((vuelo) => vuelo.colaborador)?.colaborador ||
        null;

      return {
        ...item,
        noColaborador: noColaborador ?? null,
        vuelos: vuelosDecorados,
        resumen: {
          totalVuelos,
          concluidos,
          operaciones,
          asignados,
          noShow,
          noConcluidos,
          standBy,
          pausados,
        },
      };
    });
  }, [bitacoraData]);

  useEffect(() => {
    if (!token || !bitacoraDecorada.length) return;

    const ids = bitacoraDecorada
      .map((item) => item.noColaborador)
      .filter((id) => id !== null && id !== undefined && id !== "");

    const faltantes = ids.filter((id) => !operadoresInfo[id] && !operadoresCargandoRef.current.has(id));

    if (!faltantes.length) return;

    const cargarOperadores = async () => {
      const nuevos = {};

      await Promise.all(
        faltantes.map(async (id) => {
          operadoresCargandoRef.current.add(id);
          try {
            const { data } = await axios.get(`${API_BASE_URL}api/operadores/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            nuevos[id] = data;
          } catch (error) {
            console.error(`Error al obtener operador ${id}:`, error);
          } finally {
            operadoresCargandoRef.current.delete(id);
          }
        })
      );

      if (Object.keys(nuevos).length) {
        setOperadoresInfo((prev) => ({
          ...prev,
          ...nuevos,
        }));
      }
    };

    cargarOperadores();
  }, [bitacoraDecorada, token, operadoresInfo]);

  const resumenGeneral = useMemo(() => {
    const totalOperadores = bitacoraDecorada.length;
    let totalVuelos = 0;
    let concluidos = 0;
    let asignados = 0;
    let enProceso = 0;
    let standBy = 0;
    let noShow = 0;
    let operadoresLibres = 0;

    bitacoraDecorada.forEach((item) => {
      totalVuelos += item.resumen.totalVuelos;
      concluidos += item.resumen.concluidos;
      asignados += item.resumen.asignados;
      enProceso += item.resumen.operaciones;
      standBy += item.resumen.standBy;
      noShow += item.resumen.noShow;

      const vuelosItem = item.vuelos || [];
      const libre =
        vuelosItem.length === 0 ||
        vuelosItem.every((v) => {
          const st = v.statusNormalizado || normalizarStatus(v.status);
          return st === "CONCLUIDO" || st === "NOSHOW";
        });
      if (libre) operadoresLibres += 1;
    });

    const concluidosConNoShow = concluidos + noShow;
    const pctConcluidos = totalVuelos > 0 ? Math.round((concluidosConNoShow / totalVuelos) * 100) : 0;
    const pctAsignados = totalVuelos > 0 ? Math.round((asignados / totalVuelos) * 100) : 0;
    const pctEnProceso = totalVuelos > 0 ? Math.round((enProceso / totalVuelos) * 100) : 0;

    const totalPendientes = asignados + enProceso + standBy;
    const alertaBajoRendimiento = totalVuelos > 5 && totalPendientes > concluidos;

    let semaforo = "verde";
    if (operadoresLibres <= 10) semaforo = "rojo";
    else if (operadoresLibres <= 20) semaforo = "amarillo";

    return {
      totalOperadores,
      totalVuelos,
      concluidos,
      asignados,
      enProceso,
      standBy,
      noShow,
      concluidosConNoShow,
      operadoresLibres,
      operadoresEnServicio: Math.max(totalOperadores - operadoresLibres, 0),
      semaforo,
      pctConcluidos,
      pctAsignados,
      pctEnProceso,
      alertaBajoRendimiento,
    };
  }, [bitacoraDecorada]);

  const operadoresFiltrados = useMemo(() => {
    const texto = busquedaSiglas.trim().toUpperCase();
    const vueloQuery = busquedaVuelo.trim().toUpperCase();
    const inicioVentana = moment().subtract(9, "hours");
    const finVentana = moment().endOf("day");

    return bitacoraDecorada
      .map((item) => {
        if (texto && !item.siglas?.toUpperCase().includes(texto)) {
          return null;
        }

        const vuelosVisibles = item.vuelos.filter((vuelo) => {
          const horaReferencia =
            vuelo.hora_asignacion ||
            vuelo.horaAsignacion ||
            vuelo.horaInicio ||
            vuelo.hora_inicio ||
            vuelo.horaInicioServicio ||
            vuelo.horaInicioAsignacion ||
            vuelo.horaFin ||
            vuelo.hora_fin;

          const momentoRef = parseFecha(horaReferencia);
          if (!momentoRef || !momentoRef.isBetween(inicioVentana, finVentana, undefined, "[]")) {
            return false;
          }

          if (vueloQuery && !vuelo.numero?.toString().toUpperCase().includes(vueloQuery)) return false;
          if (filtroStatus === "todos") return true;
          return vuelo.statusNormalizado === filtroStatus;
        });

        const sinServicios = vuelosVisibles.length === 0;
        const coincideFiltro = filtroStatus === "todos" || vuelosVisibles.length > 0 || sinServicios;
        if (!coincideFiltro) return null;

        return {
          ...item,
          vuelosVisibles,
        };
      })
      .filter(Boolean);
  }, [bitacoraDecorada, busquedaSiglas, filtroStatus]);

  const operadorMasOcupadoId = useMemo(() => {
    if (!operadoresFiltrados.length) return null;

    const masOcupado = operadoresFiltrados.reduce((lider, actual) => {
      if (actual.resumen.noConcluidos > lider.resumen.noConcluidos) return actual;
      return lider;
    }, operadoresFiltrados[0]);

    return masOcupado.resumen.noConcluidos > 0 ? masOcupado.noColaborador : null;
  }, [operadoresFiltrados]);

  const filasTabla = useMemo(() => {
    return operadoresFiltrados.map((item) => {
      const operadorId = item.noColaborador;
      const operadorInfo = operadorId ? operadoresInfo[operadorId] : null;
      const nombreOperador = operadorInfo
        ? [operadorInfo.nombre, operadorInfo.apellidos]
            .map((parte) => (parte || "").trim())
            .filter(Boolean)
            .join(" ")
        : null;

      const nombreFallback = item.siglas
        ? `Operador ${item.siglas}`
        : operadorId
        ? `Operador ${operadorId}`
        : "Operador";

      const servicios = (item.vuelosVisibles || []).map((vuelo) => ({
        id: vuelo.id,
        numero: vuelo.numero,
        statusEtiqueta: vuelo.statusEtiqueta,
        statusClass: vuelo.statusClass,
        horaReferencia: vuelo.hora_asignacion || vuelo.horaInicio,
        statusNormalizado: vuelo.statusNormalizado,
      }));

      const serviciosActivos = servicios.filter(
        (v) => v.statusNormalizado !== "CONCLUIDO" && v.statusNormalizado !== "NOSHOW"
      );

      // Estado del operador considerando alimentos
      const enAlimentos = item.status === "En alimentos";
      let alimentosMin = 0;
      if (enAlimentos && item.alimentosAt) {
        alimentosMin = Math.floor((Date.now() - new Date(item.alimentosAt).getTime()) / 60000);
      }
      const estaLibre = servicios.length === 0 || serviciosActivos.length === 0;
      const estadoOperador = enAlimentos
        ? "En alimentos"
        : estaLibre
        ? "Libre"
        : "En servicio";

      return {
        operadorId,
        siglas: item.siglas,
        nombreOperador: nombreOperador || nombreFallback,
        servicios,
        estadoOperador,
        enAlimentos,
        alimentosAt: item.alimentosAt || null,
        alimentosMin,
        alimentosExcedido: alimentosMin > 60,
      };
    });
  }, [operadoresFiltrados, operadoresInfo]);

  const alertas = useMemo(() => {
    const criticas = [];
    const advertencias = [];
    const felicitaciones = [];

    operadoresFiltrados.forEach((op) => {
      const { resumen, siglas } = op;
      if (resumen.totalVuelos > 0 && resumen.asignados === resumen.totalVuelos) {
        criticas.push({
          id: op.noColaborador,
          mensaje: `El usuario con siglas <strong>${siglas}</strong> tiene <strong>${resumen.asignados}</strong> servicio(s) asignados sin iniciar. ¡Presta atención!`,
        });
      } else if (resumen.totalVuelos > 0 && resumen.concluidos === resumen.totalVuelos) {
        felicitaciones.push({
          id: op.noColaborador,
          mensaje: `¡Felicidades a <strong>${siglas}</strong> por concluir todos sus servicios!`,
        });
      } else if (resumen.noConcluidos > 0) {
        advertencias.push({
          id: op.noColaborador,
          mensaje: `El usuario <strong>${siglas}</strong> tiene <strong>${resumen.noConcluidos}</strong> servicio(s) sin concluir.`,
        });
      }
    });

    return { criticas, advertencias, felicitaciones };
  }, [operadoresFiltrados]);

  const scrollToOperator = (operadorId) => {
    const cardElement = document.getElementById(`operador-card-${operadorId}`);
    if (cardElement) {
      cardElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  if (loadingPage) {
    return (
      <div className="bitacora-page">
        <div className="page-loading-overlay">
          <div className="page-loading-spinner" />
          <p className="page-loading-text">Cargando bitácora...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bitacora-page">
      <ToastContainer />

      {modalPendienteVisible && (
        <div className="modal-overlay" onClick={() => setModalPendienteVisible(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-card__header">
              <h3>🕓 Marcar servicio como pendiente</h3>
              <button className="modal-close" onClick={() => setModalPendienteVisible(false)}>
                ✖
              </button>
            </div>
            <div className="modal-card__body">
              <div className="modal-fieldset">
                <label className="modal-label">Selecciona el estatus temporal</label>
                <div className="modal-radio-group">
                  <label>
                    <input
                      type="radio"
                      value="STDBY"
                      checked={modoPendiente === "STDBY"}
                      onChange={() => setModoPendiente("STDBY")}
                    />
                    Servicio STDBY
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="PAUSE"
                      checked={modoPendiente === "PAUSE"}
                      onChange={() => setModoPendiente("PAUSE")}
                    />
                    Servicio en pausa
                  </label>
                </div>
              </div>

              <div className="modal-fieldset">
                <label className="modal-label">¿Dónde se dejó al pasajero?</label>
                <input
                  type="text"
                  value={ubicacionPax}
                  onChange={(e) => setUbicacionPax(e.target.value)}
                  placeholder="Ej. Sala 3, Migración, Aduana..."
                />
              </div>

              <div className="modal-actions">
                <button className="btn-primary" onClick={() => actualizarPendiente(servicioDetalle._id)}>
                  Confirmar actualización
                </button>
                <button className="btn-secondary" onClick={() => setModalPendienteVisible(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalServicioAbierto && servicioDetalle && (
        <div className="modal-overlay" onClick={() => setModalServicioAbierto(false)}>
          <div className="modal-card modal-card--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-card__header">
              <h2>Servicio #{servicioDetalle.noServicio}</h2>
              <span
                className={`bitacora-status-badge status-${normalizarStatus(servicioDetalle.statusServicio).toLowerCase()}`}
              >
                {servicioDetalle.statusServicio}
              </span>
              <button className="modal-close" onClick={() => setModalServicioAbierto(false)}>
                ✖
              </button>
            </div>

            <div className="modal-card__body modal-card__body--grid">
              <div className="detalle-seccion">
                <h3>🧑‍💼 Información técnica</h3>
                <p>
                  <strong>Registrado por:</strong> {servicioDetalle.usuarioInicio}
                </p>
                <p>
                  <strong>Colaborador asignado:</strong> {servicioDetalle.noColaborador}
                </p>
                <p>
                  <strong>Estación:</strong> {servicioDetalle.estacion}
                </p>
                <p>
                  <strong>Fecha:</strong> {servicioDetalle.fechaInput}
                </p>
                <p>
                  <strong>Hora inicio:</strong> {moment(servicioDetalle.horaInicio).format("HH:mm")}
                </p>
                <p>
                  <strong>Hora fin:</strong>{" "}
                  {servicioDetalle.horaFin ? moment(servicioDetalle.horaFin).format("HH:mm") : "—"}
                </p>
                <p>
                  <strong>Status:</strong> {servicioDetalle.statusServicio}
                </p>
              </div>

              <div className="detalle-seccion">
                <h3>🛫 Vuelo y pasajero</h3>
                <p>
                  <strong>Pasajero:</strong> {servicioDetalle.nombrePax}
                </p>
                <p>
                  <strong>Vuelo:</strong> {servicioDetalle.noVuelo}
                </p>
                <p>
                  <strong>Origen:</strong> {servicioDetalle.origenVuelo}
                </p>
                <p>
                  <strong>Destino:</strong> {servicioDetalle.destinoVuelo}
                </p>
                <p>
                  <strong>PNR:</strong> {servicioDetalle.pnr}
                </p>
                <p>
                  <strong>No. Asiento:</strong> {servicioDetalle.noAsiento || "—"}
                </p>
                <p>
                  <strong>Tipo silla:</strong> {servicioDetalle.tipoSilla}
                </p>
                <p>
                  <strong>Conexión:</strong> {servicioDetalle.conexion}
                </p>
                <p>
                  <strong>Conexión con:</strong> {servicioDetalle.conexionCon || "No registrado"}
                </p>
              </div>
            </div>

            {servicioDetalle.conexion === "Si" && (
              <div className="modal-footer-actions">
                <button className="btn-outline" onClick={() => abrirModalConexion(servicioDetalle)}>
                  🔗 Registrar conexión
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {mostrarModalConexion && (
        <div className="modal-overlay" onClick={cerrarModalConexion}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-card__header">
              <h3>Registrar conexión</h3>
              <button className="modal-close" onClick={cerrarModalConexion}>
                ✖
              </button>
            </div>
            <div className="modal-card__body">
              <div className="modal-fieldset">
                <label className="modal-label">Aerolínea</label>
                <input
                  type="text"
                  value={datosConexion.aerolinea}
                  onChange={(e) => setDatosConexion({ ...datosConexion, aerolinea: e.target.value })}
                  placeholder="Ej. AM"
                />
              </div>
              <div className="modal-fieldset">
                <label className="modal-label">Número de vuelo</label>
                <input
                  type="text"
                  value={datosConexion.vuelo}
                  onChange={(e) => setDatosConexion({ ...datosConexion, vuelo: e.target.value })}
                  placeholder="Ej. 128"
                />
              </div>
              <div className="modal-actions">
                <button className="btn-primary" onClick={guardarConexion}>
                  Guardar conexión
                </button>
                <button className="btn-secondary" onClick={cerrarModalConexion}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalBusquedaVueloVisible && (
        <div className="modal-overlay" onClick={() => setModalBusquedaVueloVisible(false)}>
          <div className="modal-card modal-card--wide modal-card--scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-card__header">
              <h3>Resultados para vuelo {busquedaVuelo || "—"}</h3>
              <button className="modal-close" onClick={() => setModalBusquedaVueloVisible(false)}>
                ✖
              </button>
            </div>
            <div className="modal-card__body">
              {resultadosBusquedaVuelo.length === 0 ? (
                <div className="bitacora-empty">
                  <i className="fa fa-plane-slash" aria-hidden="true" />
                  <p>No hay servicios relacionados con ese vuelo.</p>
                </div>
              ) : (
                <>
                  <div className="busqueda-vuelo-resumen">
                    <div>
                      <div className="busqueda-vuelo-resumen__titulo">
                        {resultadosBusquedaVuelo[0].aerolinea || "Aerolínea"} · Vuelo {resultadosBusquedaVuelo[0].vueloNumero}
                      </div>
                      <div className="busqueda-vuelo-resumen__ruta">
                        {resultadosBusquedaVuelo[0].origenVuelo || "Origen"} →{" "}
                        {resultadosBusquedaVuelo[0].destinoVuelo || "Destino"}
                      </div>
                      <div className="busqueda-vuelo-resumen__meta">
                        <span>
                          Hora vuelo:{" "}
                          {resultadosBusquedaVuelo[0].horaVuelo
                            ? formatearFecha(resultadosBusquedaVuelo[0].horaVuelo)
                            : "Sin dato"}
                        </span>
                        <span className="pill strong">Tipo: {resultadosBusquedaVuelo[0].tipoService || "Sin dato"}</span>
                        <span className="pill strong">Tráfico: {resultadosBusquedaVuelo[0].int_nac || "Sin dato"}</span>
                      </div>
                      <div className="busqueda-vuelo-resumen__meta">
                        <span className="pill strong">
                          Registrado por:{" "}
                          {usuariosBusqueda.length === 1
                            ? usuariosBusqueda[0]
                            : usuariosBusqueda.length > 1
                            ? usuariosBusqueda.join(", ")
                            : "Sin dato"}
                        </span>
                      </div>
                    </div>
                    <div className="busqueda-vuelo-resumen__badge">
                      {resultadosBusquedaVuelo.length} servicio(s)
                    </div>
                  </div>

                  <div className="busqueda-vuelo-grid">
                    {resultadosBusquedaVuelo.map((resultado) => (
                      <div key={resultado.servicioId} className="busqueda-vuelo-card">
                        <div className="busqueda-vuelo-card__header">
                          <div className="busqueda-vuelo-card__title">Servicio #{resultado.servicioId}</div>
                          <span className={`bitacora-status-badge ${resultado.statusClass}`}>{resultado.statusEtiqueta}</span>
                        </div>
                        <div className="busqueda-vuelo-card__body">
                          <div className="busqueda-vuelo-row busqueda-vuelo-row--highlight">
                            <span className="label">Pasajero</span>
                            <span className="value">{resultado.nombrePax || "No registrado"}</span>
                          </div>
                          <div className="busqueda-vuelo-row">
                            <span className="label">Operador</span>
                            <span className="value operador">
                              {resultado.nombreOperador}
                              {resultado.siglas && <span className="pill">Siglas {resultado.siglas}</span>}
                              {resultado.operadorId && <span className="pill">#{resultado.operadorId}</span>}
                            </span>
                          </div>
                          <div className="busqueda-vuelo-metrics">
                            <span className="pill subtle">Inicio: {resultado.horaInicioLabel}</span>
                            <span className="pill subtle">Fin: {resultado.horaFinLabel}</span>
                            <span className="pill strong">
                              {resultado.duracionLabel
                                ? `Duración: ${resultado.duracionLabel}`
                                : resultado.duracionMinutos === 0
                                ? "Duración: 0m"
                                : "Duración: Sin datos"}
                            </span>
                          </div>
                          <div className="busqueda-vuelo-row">
                            <span className="label">Silla</span>
                            <span className="value">{resultado.tipoSilla || "Sin dato"}</span>
                          </div>
                        </div>
                        <div className="busqueda-vuelo-actions">
                          <button
                            className="btn-primary"
                            type="button"
                            onClick={() => {
                              setModalBusquedaVueloVisible(false);
                              abrirDetalleServicio(resultado.servicioId);
                            }}
                          >
                            Ver detalle
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <header className="bitacora-header">
        <div className="bitacora-summary-grid">
          <article className="bitacora-summary-card total">
            <div className="summary-title">Operadores activos</div>
            <div className="summary-value">{resumenGeneral.totalOperadores}</div>
            <div className="summary-detail">
              {resumenGeneral.operadoresLibres} libres · {resumenGeneral.operadoresEnServicio} en servicio
            </div>
            <div className={`semaforo semaforo-${resumenGeneral.semaforo}`}>
              <span className="semaforo-dot" />
              <span className="semaforo-label">
                {resumenGeneral.semaforo === "verde"
                  ? "Capacidad holgada"
                  : resumenGeneral.semaforo === "amarillo"
                  ? "Capacidad media"
                  : "Capacidad crítica"}
              </span>
            </div>
          </article>
          <article className="bitacora-summary-card vuelos">
            <div className="summary-title">Servicios asignados</div>
            <div className="summary-value">{resumenGeneral.totalVuelos}</div>
            <div className="summary-detail">
              {resumenGeneral.pctAsignados}% Asignados · {resumenGeneral.pctEnProceso}% En Proceso
            </div>
          </article>
          <article className="bitacora-summary-card concluidos">
            <div className="summary-title">Completados</div>
            <div className="summary-value">{resumenGeneral.pctConcluidos}%</div>
            <div className="summary-detail">
              {resumenGeneral.concluidosConNoShow} de {resumenGeneral.totalVuelos} servicios (incluye NoShow)
            </div>
          </article>
          {resumenGeneral.alertaBajoRendimiento && (
            <article className="bitacora-summary-card alert">
              <div className="summary-title">
                <i className="fa fa-exclamation-triangle" /> Alerta de Rendimiento
              </div>
              <div className="summary-detail">El porcentaje de servicios pendientes es mayor al de los concluidos.</div>
              <div className="summary-detail-bold">¡Presta atención a tus usuarios!</div>
            </article>
          )}
        </div>
      </header>

      <section className="bitacora-controls">
        <div className="bitacora-search">
          <label htmlFor="bitacora-search">Buscar por siglas</label>
          <div className="bitacora-search__box">
            <i className="fa fa-search" aria-hidden="true" />
            <input
              id="bitacora-search"
              type="text"
              value={busquedaSiglas}
              onChange={(e) => setBusquedaSiglas(e.target.value.toUpperCase())}
              placeholder="Ej. JDO, MLO"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="bitacora-search">
          <label htmlFor="bitacora-flight-search">Buscar por vuelo</label>
          <div className="bitacora-search__box bitacora-search__box--with-button">
            <i className="fa fa-plane-departure" aria-hidden="true" />
            <input
              id="bitacora-flight-search"
              type="text"
              value={busquedaVuelo}
              onChange={(e) => setBusquedaVuelo(e.target.value.toUpperCase())}
              placeholder="Ej. 498, 2200"
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  buscarPorVuelo();
                }
              }}
            />
            <button type="button" className="btn-search" onClick={buscarPorVuelo}>
              Buscar
            </button>
          </div>
        </div>

        <div className="bitacora-filter-group">
          <span className="filter-label">Filtrar por estado</span>
          <div className="bitacora-chip-group">
            {STATUS_FILTERS.map(({ valor, etiqueta }) => (
              <button
                key={valor}
                type="button"
                className={`bitacora-chip ${filtroStatus === valor ? "activo" : ""}`}
                onClick={() => setFiltroStatus(valor)}
              >
                {etiqueta}
              </button>
            ))}
          </div>
        </div>
        <div className="bitacora-view-toggle">
          <div>
            <span className="filter-label">Vista</span>
            <p className="bitacora-view-toggle__hint">Intercambia entre tarjetas y una tabla compacta.</p>
          </div>
          <div className="bitacora-view-toggle__buttons">
            <button
              type="button"
              className={`view-toggle-btn ${vista === "tarjetas" ? "activo" : ""}`}
              onClick={() => setVista("tarjetas")}
            >
              Tarjetas
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${vista === "tabla" ? "activo" : ""}`}
              onClick={() => setVista("tabla")}
            >
              Tabla
            </button>
          </div>
        </div>
        <div className="bitacora-status-legend">
          <div className="legend-title">Guía de colores</div>
          <div className="legend-items">
            <span className="legend-item status-concluido">Concluido</span>
            <span className="legend-item status-enproceso">En proceso</span>
            <span className="legend-item status-asignado">Asignado</span>
            <span className="legend-item status-stdby">Stand by</span>
            <span className="legend-item status-pause">En pausa</span>
            <span className="legend-item status-noshow">No show</span>
          </div>
        </div>
      </section>

      {vista === "tarjetas" ? (
        <section className="bitacora-card-grid">
          {operadoresFiltrados.length === 0 ? (
            <div className="bitacora-empty">
              <i className="fa fa-user-clock" aria-hidden="true" />
              <p>No encontramos operadores con los filtros aplicados.</p>
              <span>Intenta limpiar la búsqueda o seleccionar otro estado.</span>
            </div>
          ) : (
            operadoresFiltrados.map((item) => {
              const operadorId = item.noColaborador;
              const operadorInfo = operadorId ? operadoresInfo[operadorId] : null;
              const nombreOperador = operadorInfo
                ? [operadorInfo.nombre, operadorInfo.apellidos]
                    .map((parte) => (parte || "").trim())
                    .filter(Boolean)
                    .join(" ")
                : null;
              const statusOperador = operadorInfo?.status?.trim();

              const esMasOcupado = operadorId && operadorId === operadorMasOcupadoId;
              const todoConcluido = item.resumen.totalVuelos > 0 && item.resumen.concluidos === item.resumen.totalVuelos;

              const todoAsignado = item.resumen.totalVuelos > 0 && item.resumen.asignados === item.resumen.totalVuelos;

              const cardClassName = ["bitacora-card", todoAsignado ? "bitacora-card--all-assigned" : esMasOcupado ? "bitacora-card--highlight" : "", todoConcluido ? "bitacora-card--all-concluded" : ""]
                .filter(Boolean)
                .join(" ");

              return (
                <article key={`${item.noFila}-${item.siglas}`} id={`operador-card-${operadorId}`} className={cardClassName}>
                  <header className="bitacora-card__header">
                    <div className="bitacora-card__identity">
                      <div className="bitacora-card__identity-row">
                        {item.siglas && <span className="bitacora-card__siglas">{item.siglas}</span>}
                        {operadorId && <span className="bitacora-card__pill">#{operadorId}</span>}
                        {statusOperador && (
                          <span className={`bitacora-status status-${statusOperador.toLowerCase().replace(/\s+/g, "-")}`}>
                            {statusOperador}
                          </span>
                        )}
                      </div>
                      <h2 className="bitacora-card__name">{nombreOperador || `Operador ${item.siglas || ""}`.trim()}</h2>
                    </div>
                    <div className="bitacora-card__kpi">
                      <span>Total servicios</span>
                      <strong>{item.resumen.totalVuelos}</strong>
                    </div>
                  </header>

                  <div className="bitacora-card__badges">
                    <span className="bitacora-badge">
                      Concluidos: <strong>{item.resumen.concluidos}</strong>
                    </span>
                    <span className="bitacora-badge">
                      En proceso: <strong>{item.resumen.operaciones}</strong>
                    </span>
                    <span className="bitacora-badge">
                      Stand by: <strong>{item.resumen.standBy}</strong>
                    </span>
                    {item.resumen.pausados > 0 && (
                      <span className="bitacora-badge">
                        Pausados: <strong>{item.resumen.pausados}</strong>
                      </span>
                    )}
                    {item.resumen.noShow > 0 && (
                      <span className="bitacora-badge noshow">
                        No Show: <strong>{item.resumen.noShow}</strong>
                      </span>
                    )}
                  </div>

                  <div className="bitacora-card__flights">
                    {item.vuelosVisibles.length === 0 ? (
                      <div className="bitacora-flight bitacora-flight--empty">
                        <span className="bitacora-flight__number">Sin servicios asignados</span>
                      </div>
                    ) : (
                      item.vuelosVisibles.map((vuelo) => (
                        <button
                          key={vuelo.id}
                          type="button"
                          className={`bitacora-flight ${vuelo.statusClass}`}
                          title={`Servicio ${vuelo.numero} · ${vuelo.statusEtiqueta}`}
                          onClick={() => abrirDetalleServicio(vuelo.id)}
                        >
                          <span className="bitacora-flight__number">{vuelo.numero}</span>
                          {!["CONCLUIDO", "NOSHOW"].includes(vuelo.statusNormalizado) && (
                            <TimeAgo timestamp={vuelo.hora_asignacion || vuelo.horaInicio} />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </article>
              );
            })
          )}
        </section>
      ) : (
        <section className="bitacora-table-section">
          <div className="bitacora-table-header">
            <div>
              <h3>Vista en tabla</h3>
              <p>Supervisión compacta de los servicios en turno con los filtros activos.</p>
            </div>
            <span className="bitacora-table-count">{filasTabla.length} operadores visibles</span>
          </div>
          {filasTabla.length === 0 ? (
            <div className="bitacora-empty">
              <i className="fa fa-table" aria-hidden="true" />
              <p>No hay operadores para mostrar en la tabla.</p>
              <span>Modifica los filtros o regresa a la vista en tarjetas.</span>
            </div>
          ) : (
            <div className="bitacora-table-wrapper">
              <table className="bitacora-table">
                <thead>
                  <tr>
                    <th>Operador</th>
                    <th>Servicios</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filasTabla.map((fila) => (
                    <tr key={`${fila.operadorId || fila.siglas || "sin-id"}`}>
                      <td>
                        <div className="table-cell__stack">
                          <span className="table-cell__title">{fila.nombreOperador}</span>
                          <span className="table-cell__pill">
                            {fila.siglas ? `Siglas ${fila.siglas}` : "Sin siglas"}
                            {fila.operadorId ? ` · ID ${fila.operadorId}` : ""}
                          </span>
                        </div>
                      </td>
                      <td>
                        {fila.servicios.length === 0 ? (
                          <span className="table-cell__subtitle">Sin servicios asignados</span>
                        ) : (
                          <div className="table-cell__chips">
                            {fila.servicios.map((servicio) => (
                              <button
                                key={servicio.id}
                                type="button"
                                className={`table-chip ${servicio.statusClass}`}
                                onClick={() => abrirDetalleServicio(servicio.id)}
                                title={`Servicio ${servicio.numero} · ${servicio.statusEtiqueta}`}
                              >
                                <span className="table-chip__title">{servicio.numero || "Sin vuelo"}</span>
                                <span className="table-chip__status">{servicio.statusEtiqueta}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className={`bitacora-table__status ${
                            fila.enAlimentos
                              ? fila.alimentosExcedido
                                ? "status-alimentos-excedido"
                                : "status-alimentos"
                              : fila.estadoOperador === "Libre"
                              ? "status-concluido"
                              : "status-enproceso"
                          }`}
                          title={
                            fila.enAlimentos
                              ? `En alimentos desde hace ${fila.alimentosMin} min${fila.alimentosExcedido ? " — excedió 1 hora" : ""}`
                              : fila.estadoOperador
                          }
                        >
                          {fila.enAlimentos ? (
                            <>
                              🍽 En alimentos
                              <span className="alimentos-timer">
                                {fila.alimentosMin}min
                                {fila.alimentosExcedido && " ⚠"}
                              </span>
                            </>
                          ) : (
                            fila.estadoOperador
                          )}
                        </span>
                      </td>
                      <td className="bitacora-table__actions">
                        <button
                          type="button"
                          className="btn-table"
                          onClick={() => scrollToOperator(fila.operadorId)}
                          disabled={!fila.operadorId}
                        >
                          Ir a tarjeta
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <aside className="bitacora-alerts-sidebar">
        {alertas.criticas.length > 0 && (
          <div className="alerts-section">
            <h3>
              <i className="fa fa-exclamation-triangle" /> Alertas Críticas
            </h3>
            <div className="alerts-section__list">
              {alertas.criticas.map((alerta) => (
                <div key={alerta.id} className="alert-card alert-card--critical" onClick={() => scrollToOperator(alerta.id)}>
                  <p dangerouslySetInnerHTML={{ __html: alerta.mensaje }} />
                </div>
              ))}
            </div>
          </div>
        )}
        {alertas.felicitaciones.length > 0 && (
          <div className="alerts-section">
            <h3>
              <i className="fa fa-star" /> Felicitaciones
            </h3>
            <div className="alerts-section__list">
              {alertas.felicitaciones.map((alerta) => (
                <div key={alerta.id} className="alert-card alert-card--success" onClick={() => scrollToOperator(alerta.id)}>
                  <p dangerouslySetInnerHTML={{ __html: alerta.mensaje }} />
                </div>
              ))}
            </div>
          </div>
        )}
        {alertas.advertencias.length > 0 && (
          <div className="alerts-section">
            <h3>
              <i className="fa fa-exclamation-circle" /> Atención Requerida
            </h3>
            <div className="alerts-section__list">
              {alertas.advertencias.map((alerta) => (
                <div key={alerta.id} className="alert-card alert-card--warning" onClick={() => scrollToOperator(alerta.id)}>
                  <p dangerouslySetInnerHTML={{ __html: alerta.mensaje }} />
                </div>
              ))}
            </div>
          </div>
        )}
        {alertas.criticas.length === 0 && alertas.felicitaciones.length === 0 && alertas.advertencias.length === 0 && (
          <div className="alerts-sidebar__empty">No hay alertas por el momento.</div>
        )}
      </aside>
    </div>
  );
};

export default BitacoraPage;
